/* global BigInt */
// HERE Maps client (frontend) — appelle UNIQUEMENT le proxy backend
// /api/here/* pour éviter d'exposer la clé HERE_API_KEY dans le bundle.
//
// Endpoints proxifiés :
//   GET /api/here/autocomplete
//   GET /api/here/geocode
//   GET /api/here/reverse-geocode
//   GET /api/here/route
//
// + utilitaire decodeFlexPolyline : décodeur du format polyline HERE
//   (https://github.com/heremaps/flexible-polyline) — retourne un tableau
//   de [lat, lng] utilisable directement par Leaflet.

// Réutilise l'instance axios partagée (qui a déjà le Bearer token Keycloak
// configuré via setAuthToken). Indispensable sinon le proxy /api/here/*
// renvoie 401 Unauthorized.
import { api } from '../services/api';

export async function autocomplete(q, { limit = 8, at } = {}) {
  if (!q || !q.trim()) return [];
  const { data } = await api.get('/here/autocomplete', { params: { q, limit, at, lang: 'fr-FR' } });
  return Array.isArray(data.items) ? data.items : [];
}

export async function geocode(q) {
  const { data } = await api.get('/here/geocode', { params: { q, lang: 'fr-FR' } });
  return Array.isArray(data.items) ? data.items : [];
}

export async function reverseGeocode(lat, lng) {
  const { data } = await api.get('/here/reverse-geocode', { params: { at: `${lat},${lng}`, lang: 'fr-FR' } });
  return Array.isArray(data.items) ? data.items : [];
}

// options : via ([[lat,lng], ...]), routingMode ('fast'|'short'), alternatives (0..3),
// avoid (['tollRoad', 'controlledAccessHighway', 'ferry']), departureTime ('AAAA-MM-JJTHH:mm')
export async function route(origin, destination, {
  transportMode = 'car', via = [], routingMode, alternatives, avoid = [], departureTime,
} = {}) {
  const params = {
    origin:      `${origin[0]},${origin[1]}`,
    destination: `${destination[0]},${destination[1]}`,
    transportMode,
  };
  if (via.length) params.via = via.map((p) => `${p[0]},${p[1]}`).join(';');
  if (routingMode) params.routingMode = routingMode;
  if (alternatives) params.alternatives = alternatives;
  if (avoid.length) params.avoid = avoid.join(',');
  if (departureTime) params.departureTime = departureTime;
  const { data } = await api.get('/here/route', { params });
  return data;
}

// ──────────────────────────────────────────────────────────────────────
// Flexible Polyline decoder
// Adapté de https://github.com/heremaps/flexible-polyline (licence MIT)
// Retourne un tableau [[lat, lng], ...] depuis une chaîne polyline encodée.
// ──────────────────────────────────────────────────────────────────────

// Table officielle (cf. https://github.com/heremaps/flexible-polyline/blob/master/javascript/src/index.js)
const DECODING_TABLE = [
  62, -1, -1, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, -1, -1, -1,
  -1, -1, -1, -1,  0,  1,  2,  3,  4,  5,  6,  7,  8,  9, 10, 11,
  12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, -1, -1,
  -1, -1, 63, -1, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37,
  38, 39, 40, 41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51,
];

function decodeChar(c) {
  const code = c.charCodeAt(0) - 45;
  return code >= 0 && code < DECODING_TABLE.length ? DECODING_TABLE[code] : -1;
}

function decodeUnsignedVarint(encoded, idx) {
  let value = 0n;
  let shift = 0n;
  let i = idx.value;
  while (i < encoded.length) {
    const v = decodeChar(encoded[i]);
    if (v < 0) throw new Error(`Invalid char ${encoded[i]}`);
    value |= BigInt(v & 0x1F) << shift;
    i++;
    if ((v & 0x20) === 0) break;
    shift += 5n;
  }
  idx.value = i;
  return value;
}

function decodeSignedVarint(encoded, idx) {
  const u = decodeUnsignedVarint(encoded, idx);
  const isNeg = (u & 1n) !== 0n;
  let shifted = u >> 1n;
  if (isNeg) shifted = ~shifted;
  return Number(shifted);
}

/**
 * Decode a HERE flexible polyline string into [[lat, lng], …].
 */
export function decodeFlexPolyline(encoded) {
  if (!encoded || typeof encoded !== 'string') return [];
  const idx = { value: 0 };

  // Header v1
  const header = decodeUnsignedVarint(encoded, idx);
  const version = Number(header & 0xFn);
  if (version !== 1) throw new Error('Unsupported flexpolyline version: ' + version);

  const secondByte = decodeUnsignedVarint(encoded, idx);
  const precision    = Number(secondByte & 0xFn);
  const thirdDim     = Number((secondByte >> 4n) & 0x7n);
  const thirdPrec    = Number((secondByte >> 7n) & 0xFn);

  const factor = Math.pow(10, precision);
  const factor3D = Math.pow(10, thirdPrec);
  const hasThirdDim = thirdDim !== 0;

  let lat = 0, lng = 0, z = 0;
  const out = [];
  while (idx.value < encoded.length) {
    lat += decodeSignedVarint(encoded, idx);
    lng += decodeSignedVarint(encoded, idx);
    if (hasThirdDim) z += decodeSignedVarint(encoded, idx);
    out.push([lat / factor, lng / factor]);
  }
  return out;
}

export default { autocomplete, geocode, reverseGeocode, route, decodeFlexPolyline };
